import CRMService from "@/src/api/CRMService.js";
import { standardObjects } from "@/src/constants/objectConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import {
  isDynamicCRMEnabled,
  isStandardObjectsV2Enabled,
} from "@/src/utils/dynamicFormUtils.js";
import eventBus from "@/src/utils/eventBus.js";

let dynamicObjectsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000;

const findStandardObject = (routeName) =>
  standardObjects.find(
    (obj) => obj.viewSlug === routeName || obj.slug === routeName,
  );

const getDynamicObjectsSlugs = async () => {
  const now = Date.now();

  if (
    dynamicObjectsCache &&
    cacheTimestamp &&
    now - cacheTimestamp < CACHE_DURATION
  ) {
    return dynamicObjectsCache;
  }

  try {
    if (!isDynamicCRMEnabled()) {
      dynamicObjectsCache = new Set();
      cacheTimestamp = now;
      return dynamicObjectsCache;
    }

    const response = await CRMService.fetchDynamicCRMListObjects();
    if (response?.success && response?.objects) {
      const standardSlugSet = new Set(standardObjects.map((o) => o.slug));

      const slugs = response.objects
        .filter((obj) => {
          // Exclude tasks and tickets
          if (
            obj.slug === entityTypes.TASKS ||
            obj.slug === entityTypes.TICKETS
          ) {
            return false;
          }

          // If standard_objects_v2 is disabled, exclude standard objects from API
          if (!isStandardObjectsV2Enabled() && standardSlugSet.has(obj.slug)) {
            return false;
          }

          return true;
        })
        .map((obj) => obj.slug);

      dynamicObjectsCache = new Set(slugs);
      cacheTimestamp = now;
      return dynamicObjectsCache;
    }
  } catch (error) {
    console.error("Error fetching dynamic CRM objects:", error);
  }

  dynamicObjectsCache = new Set();
  cacheTimestamp = now;
  return dynamicObjectsCache;
};

export const shouldRedirectToCrmRoute = async (routeName) => {
  const standardObject = findStandardObject(routeName);
  if (!standardObject) return false;

  if (!isStandardObjectsV2Enabled()) return false;

  const dynamicSlugs = await getDynamicObjectsSlugs();
  return dynamicSlugs.has(standardObject.slug);
};

export const getCrmRoutePath = (routeName) => {
  const standardObject = findStandardObject(routeName);
  return standardObject ? `/crm/${standardObject.slug}` : null;
};

const clearDynamicObjectsCache = () => {
  dynamicObjectsCache = null;
  cacheTimestamp = null;
};

eventBus.on("dynamic-crm-objects-updated", clearDynamicObjectsCache);
